import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';
import { User } from '@/types/User.ts';
import { useStorage } from '@vueuse/core';
import { Api } from '@/utils/api.ts';
import { UserResponse } from '@/types/Responses/UserResponse.ts';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.ts';
import { eurocentFormat } from '@/utils/format.ts';

export const useOnboardingStore = defineStore('onboarding', () => {
	const authStore = useAuthStore();
	const router = useRouter();
	const user: Ref<User> = ref({
		id: '',
		email: [],
		phone: [],
		totpRecovery: [],
		fridom_user_id: null,
		user_id: null,
		customer_user_id: null,
		firstname: '',
		lastname: '',
		birthdate: '',
		language: 'en',
		password_status: 0,
		last_password_change: null,
		mfa_email: '',
		mfa_phone: '',
		mfa_forced: false,
		mfa_customer: false,
		has_sso: false,
		sso_only: false,
		partner_user_id: null,
		merchant_user_id: null,
	});
	const bizzAmount: Ref<string | null> = ref(null);

	const getUserInfos = async () => {
		const response = await Api.get('/user');

		if (!response.ok) {
			throw new Error('Network response was not ok');
		}

		const responseBody = await (response.json() as Promise<UserResponse>);

		user.value = responseBody.data;

		return responseBody;
	};

	const registerUser = async (recaptcha: string, code?: string, cardNumber?: string) => {
		const body = {
			recaptcha,
			...(cardNumber ? { pan: cardNumber } : { code }),
		};
		const response = await fetch(`${Api.commonUrl}/user/register`, {
			method: 'POST',
			headers: { ...Api.commonHeaders, 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		});
		const data = await response.json().catch(() => ({}));
		if (!response.ok) {
			throw { status: response.status, message: data?.message, ...data };
		}
		if (data.bizzGiftAmount) {
			bizzAmount.value = eurocentFormat(data.bizzGiftAmount);
		}
		await saveOnboardingToken(data.access_token, cardNumber || undefined);
		router.push({ name: 'onboarding.create' });
	};

	// Entry point of the MFA setup phase (reached after the personal-info / terms steps). An invitation code is
	// clicked from the user's email, so ownership is proven and we jump straight to method selection; the card
	// flow has no such proof, so it verifies an email first.
	const mfaEntryStep = (): string => (useStorage('card', '').value ? 'onboarding.email-verification' : 'onboarding-mfa-selection');

	const saveOnboardingToken = async (access_token: string, card?: string) => {
		if (card) {
			const code = useStorage('card', '');
			code.value = card;
		}
		authStore.token = access_token;
		const token = useStorage('onboarding_token', '');
		token.value = access_token;
		// Mark the flow as in-progress for the whole duration. The router uses this (with the persisted step)
		// to keep the user in the mandatory flow across refreshes, returns and reconnections until it completes.
		useStorage('onboarding_pending', '').value = '1';
		await getUserInfos();
	};

	const deleteOnboardingToken = async (): Promise<void> => {
		try {
			const token = useStorage('onboarding_token', '');
			if (token.value) {
				authStore.token = token.value;
				token.value = null;
				await Api.delete('/token', { token: authStore.token });
				authStore.token = '';
			}
		} catch (e) {
			console.error(e);
		}
	};

	const restoreOnboardingToken = async (): Promise<void> => {
		const token = useStorage('onboarding_token', '');
		if (!token.value) {
			return;
		}
		authStore.token = token.value;
		await getUserInfos();
	};

	const finalizeOnboarding = async (password: string) => {
		const code = useStorage('card', '');
		try {
			const response = await Api.post('/user/register/finalize', {
				client_id: import.meta.env.VITE_OAUTH_API_CLIENT_ID_PASSWORD_GRANT,
				password,
				...(code.value && { pan: code.value }),
			});
			const data = await response.json();

			await authStore.loginByToken(data.access_token);
			// The real session is now active and the backend already revoked the onboarding token on finalize.
			// Drop the onboarding token so exitOnboarding's cleanup can't wipe the live session token. The
			// onboarding_pending marker stays set: the post-password steps below are mandatory, so the router
			// keeps the user here (even after a refresh/reconnect) until the flow completes.
			useStorage('onboarding_token', '').value = null;
			// Personal info (incl. the birthdate identity check) and, for beneficiaries, the terms/optin step
			// were already completed before this step, so finalizing the password leads straight to confirmation.
			router.push({ name: 'onboarding.confirmation' });
		} finally {
			code.value = null;
		}
	};

	const exitOnboarding = async (): Promise<void> => {
		try {
			await deleteOnboardingToken();
			localStorage.removeItem('onboarding_token');
			localStorage.removeItem('onboarding_skip_password');
			localStorage.removeItem('card');
			sessionStorage.removeItem('sms_payload');
			sessionStorage.removeItem('email_payload');
		} catch (e) {
			console.error('Error during key removal from storage', e);
		}
	};

	// Releases the mandatory-flow lock. Called only on genuine completion (the final success screen) or on a
	// hard reset (expired token). Deliberately separate from exitOnboarding, which runs on incidental route
	// leaves and must NOT clear the lock — otherwise the user could escape the mandatory steps by navigating away.
	const completeOnboarding = (): void => {
		useStorage('onboarding_pending', '').value = null;
		useStorage('onboarding_step', '').value = null;
	};

	return {
		//DATA
		user,
		bizzAmount,

		//FUNCTIONS
		getUserInfos,
		registerUser,
		mfaEntryStep,
		saveOnboardingToken,
		deleteOnboardingToken,
		restoreOnboardingToken,
		finalizeOnboarding,
		exitOnboarding,
		completeOnboarding,
	};
});
